import { useState, useEffect, useRef } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Header } from '@/components/layout/Header'
import { ProviderBadge } from '@/components/user'
import { useProfile } from '@/hooks/useProfile'
import { usePageTitle } from '@/hooks/useDocumentHead'
import { usePushNotifications } from '@/hooks/usePushNotifications'
import { AlertCircle, Loader2, UserPlus, Upload, X, Camera, Mail, Shield, Bell, Smartphone, Trash2, Plus, Chrome, Globe } from 'lucide-react'
import { imageFileToSquarePngDataUrl } from '@/utils/image'

// Push Notifications Section Component
function PushNotificationsSection() {
  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    devices,
    enable,
    disable,
    removeDeviceById,
  } = usePushNotifications()
  
  const [isEnabling, setIsEnabling] = useState(false)
  const [isDisabling, setIsDisabling] = useState(false)
  const [removingDeviceId, setRemovingDeviceId] = useState<string | null>(null)

  const handleEnable = async () => {
    setIsEnabling(true)
    await enable()
    setIsEnabling(false)
  }

  const handleDisable = async () => {
    setIsDisabling(true)
    await disable()
    setIsDisabling(false)
  }

  const handleRemoveDevice = async (deviceId: string) => {
    setRemovingDeviceId(deviceId)
    await removeDeviceById(deviceId)
    setRemovingDeviceId(null)
  }

  const getBrowserIcon = (browser?: string) => {
    if (browser?.toLowerCase().includes('chrome')) return Chrome
    return Globe
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  if (!isSupported) {
    return (
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Push Notifications</h3>
        <div className="flex items-center gap-3 py-2">
          <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
            <Bell className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">Not Available</p>
            <p className="text-xs text-muted-foreground">
              Push notifications are not supported in this browser.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Push Notifications</h3>
        {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {/* Permission Status */}
      {permission === 'denied' && (
        <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <p>Notifications are blocked. Please enable them in your browser settings.</p>
          </div>
        </div>
      )}

      {/* Current Device Status */}
      <div className="flex items-center gap-3 py-2">
        <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
          <Bell className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground">This Device</p>
          <p className="text-sm font-medium">
            {isSubscribed ? 'Notifications enabled' : 'Notifications disabled'}
          </p>
        </div>
        <Button
          size="sm"
          variant={isSubscribed ? 'outline' : 'default'}
          onClick={isSubscribed ? handleDisable : handleEnable}
          disabled={isEnabling || isDisabling || permission === 'denied'}
          className="h-8"
        >
          {isEnabling || isDisabling ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : isSubscribed ? (
            'Disable'
          ) : (
            <>
              <Plus className="h-3.5 w-3.5 mr-1" />
              Enable
            </>
          )}
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Registered Devices */}
      {devices.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Registered Devices ({devices.length})</p>
          <div className="space-y-2">
            {devices.map((device) => {
              const BrowserIcon = getBrowserIcon(device.browser)
              return (
                <div
                  key={device.deviceId}
                  className="flex items-center gap-3 py-2 px-3 rounded-lg bg-muted/50"
                >
                  <div className="h-8 w-8 rounded-lg bg-background flex items-center justify-center">
                    <Smartphone className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">
                        {device.deviceName || `${device.browser || 'Browser'} on ${device.platform || 'Unknown'}`}
                      </p>
                      <BrowserIcon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Added {formatDate(device.createdAt)}
                      {device.lastUsedAt && device.lastUsedAt !== device.createdAt && (
                        <> • Last used {formatDate(device.lastUsedAt)}</>
                      )}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => handleRemoveDevice(device.deviceId)}
                    disabled={removingDeviceId === device.deviceId}
                    title="Remove device"
                  >
                    {removingDeviceId === device.deviceId ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Help text */}
      <p className="text-xs text-muted-foreground">
        Enable push notifications to receive instant alerts about match results, challenges, and updates.
      </p>
    </div>
  )
}

export function Profile() {
  usePageTitle('Profile')
  const location = useLocation()
  const { profile, isLoading, needsCreate, updateProfile } = useProfile()
  const [displayName, setDisplayName] = useState('')
  const [picturePreview, setPicturePreview] = useState<string | null>(null)
  const [hasNewPicture, setHasNewPicture] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.displayName || '')
      setPicturePreview(profile.picture || null)
      setHasNewPicture(false)
    }
  }, [profile])

  const MAX_FILE_SIZE = 2.5 * 1024 * 1024 // 2.5MB

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > MAX_FILE_SIZE) {
      setError(`File size must be less than 2.5MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`)
      return
    }

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file')
      return
    }

    setError(null)

    try {
      const dataUrl = await imageFileToSquarePngDataUrl(file, 256)
      setPicturePreview(dataUrl)
      setHasNewPicture(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process image')
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleRemovePicture = () => {
    setHasNewPicture(false)
    setPicturePreview(profile?.picture || null)
  }

  const handleSave = async () => {
    setIsSaving(true)
    setError(null)

    try {
      await updateProfile({
        displayName: displayName.trim() || undefined,
        picture: hasNewPicture ? (picturePreview || undefined) : undefined,
      })
      setHasNewPicture(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save profile')
    } finally {
      setIsSaving(false)
    }
  }

  const hasChanges = hasNewPicture || displayName !== (profile?.displayName || '')

  // Show loading state
  if (isLoading && !profile && !needsCreate) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </main>
      </div>
    )
  }

  // New user - redirect to signup
  if (needsCreate) {
    return <Navigate to="/signup" state={{ from: location }} replace />
  }

  // Existing user - show edit profile
  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold">Profile Settings</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage your account information</p>
        </div>

        {/* Profile Card with Avatar Hero */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          {/* Avatar Section - Hero area */}
          <div className="relativex px-6 py-8">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              {/* Avatar with hover overlay */}
              <div className="relative group">
                {picturePreview ? (
                  <div className="relative">
                    <img
                      src={picturePreview}
                      alt="Profile"
                      className="h-28 w-28 rounded-full object-cover ring-2 ring-background shadow-xl"
                    />
                    {hasNewPicture && (
                      <button
                        onClick={handleRemovePicture}
                        className="absolute -top-1 -right-1 h-7 w-7 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/90 shadow-lg transition-transform hover:scale-110"
                        type="button"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="h-28 w-28 rounded-full bg-muted flex items-center justify-center ring-4 ring-background shadow-xl">
                    <UserPlus className="h-10 w-10 text-muted-foreground" />
                  </div>
                )}
                {/* Hover overlay for upload */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="profile-picture-upload"
                />
                <label
                  htmlFor="profile-picture-upload"
                  className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex items-center justify-center"
                >
                  <Camera className="h-6 w-6 text-white" />
                </label>
              </div>

              {/* Name & Email preview */}
              <div className="text-center sm:text-left flex-1">
                <h2 className="text-xl font-semibold">{profile?.displayName || 'No name set'}</h2>
                <p className="text-sm text-muted-foreground">{profile?.email}</p>
                <div className="mt-3">
                  <Label htmlFor="profile-picture-upload" className="cursor-pointer">
                    <Button variant="outline" size="sm" type="button" className="h-8" asChild>
                      <span>
                        <Upload className="h-3.5 w-3.5 mr-1.5" />
                        {picturePreview ? 'Change Photo' : 'Upload Photo'}
                      </span>
                    </Button>
                  </Label>
                </div>
              </div>
            </div>
          </div>

          {/* Form Fields */}
          <div className="p-6 space-y-6">
            {/* Display Name Field */}
            <div className="space-y-2">
              <Label htmlFor="display-name" className="text-sm font-medium">Display Name</Label>
              <Input
                id="display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your display name"
                maxLength={50}
                className="max-w-md"
              />
              <p className="text-xs text-muted-foreground">This is how other players will see you on leagues</p>
            </div>

            {/* Divider */}
            <div className="border-t border-border" />

            {/* Read-only Info Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Account Information</h3>
              
              {/* Email */}
              <div className="flex items-center gap-3 py-2">
                <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Email Address</p>
                  <p className="text-sm font-medium truncate">{profile?.email || 'Not available'}</p>
                </div>
              </div>

              {/* Auth Provider */}
              {profile?.provider && (
                <div className="flex items-center gap-3 py-2">
                  <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">Sign-in Method</p>
                    <div className="mt-0.5">
                      <ProviderBadge provider={profile.provider} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="border-t border-border" />

            {/* Push Notifications Section */}
            <PushNotificationsSection />

            {/* Error Message */}
            {error && (
              <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
          </div>

          {/* Footer with Save Button */}
          <div className="px-6 py-4 bg-muted/30 border-t border-border flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {hasChanges ? 'You have unsaved changes' : 'All changes saved'}
            </p>
            <Button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  )
}
